import React, { useEffect, useState } from 'react';
import { useMainMedicalExpensePrices } from '@/hooks/main/useMainMedicalExpensePrice';
import {
  AppTextField,
  AppPagination,
  AppTypography,
  AppCard,
  AppSpace,
  AppRow,
  AppCol,
  AppSpin,
} from '@/components/atoms';
import { CollapsibleCard, InfoRow } from '@/components/molecules';
import { convGtJDate } from '@/utils/dateConverter';
import { useTheme } from '@/shared/providers/theme';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import type { MedicalExpensePrice } from './types';

const LAW_REVISION_DATE = '2024-10-01';
// 令和8年法改正 — 施術 from this date splits the old open-ended 訪問施術料4 (10人以上)
// into 4 (10〜19人) and 5 (20人以上), and adds 明細書発行加算.
const LAW_REVISION_DATE_2026 = '2026-07-01';
const LAW_REVISION_DATE_JUN_2024_START = '2024-06-01';
const LAW_REVISION_DATE_JUN_2024_END = '2024-09-30';
const SENTINEL_END_DATE = '2100-12-31';
const circledBand = ['①', '②', '③', '④', '⑤'] as const;

const formatAvailableDate = (value: string | null | undefined): string => {
  if (!value || value === SENTINEL_END_DATE) return '';
  return convGtJDate(value);
};

const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '―';
  return `${value.toLocaleString('ja-JP')} 円`;
};

const isAfterOrEqualLawRevision = (availableFrom: string | null | undefined): boolean => {
  if (!availableFrom) return false;
  return availableFrom >= LAW_REVISION_DATE;
};

const isAfterOrEqualLawRevision2026 = (availableFrom: string | null | undefined): boolean => {
  if (!availableFrom) return false;
  return availableFrom >= LAW_REVISION_DATE_2026;
};

const isLawRevisionJunToSep2024 = (availableFrom: string | null | undefined): boolean => {
  if (!availableFrom) return false;
  return (
    availableFrom >= LAW_REVISION_DATE_JUN_2024_START &&
    availableFrom <= LAW_REVISION_DATE_JUN_2024_END
  );
};

const MedicalExpensePricePage: React.FC = () => {
  const [page, setPage] = useState(1);
  const { serviceType } = useTheme();
  const isA2 = serviceType === SERVICE_TYPE_A2;

  useEffect(() => {
    setPage(1);
  }, [serviceType]);

  // One record per page for both services. A2 keeps 1術 and 2術 on separate pages —
  // the same as A2 production, where each page is a single treatment_type and the
  // 術数 is therefore a property of the page, not a column on every row.
  const { data, isError, isLoading, isFetching } = useMainMedicalExpensePrices(1, page);

  const items = data?.data.data ?? [];
  const display = (items[0] ?? {}) as MedicalExpensePrice;

  if (isError) {
    return (
      <div className="px-4 py-3 text-red-700 border border-red-200 rounded bg-red-50">
        <AppTypography.P>データの取得に失敗しました。</AppTypography.P>
      </div>
    );
  }

  const showNewLaw = isAfterOrEqualLawRevision(display.available_from);

  const showNewLawJun2024 = isLawRevisionJunToSep2024(display.available_from);

  // 令和8年法改正 added 訪問施術料5 and 明細書発行加算. Older periods have neither, so the
  // band list is derived from the period rather than hard-coded — showing an empty ⓹ row
  // on a 2024 period would imply a rate that never existed.
  const isRevision2026 = isAfterOrEqualLawRevision2026(display.available_from);
  const visitTreatmentBands: readonly (1 | 2 | 3 | 4 | 5)[] = isRevision2026
    ? ([1, 2, 3, 4, 5] as const)
    : ([1, 2, 3, 4] as const);

  // A2: treatment price based on type
  const treatmentPriceAcuAll =
    display.treatment_type === 2 ? formatNumber(display.treatment_price) : '―';
  const treatmentPriceAcuOnly =
    display.treatment_type === 1 ? formatNumber(display.treatment_price) : '―';

  if (isLoading) {
    return (
      <div
        className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-md bg-white/80"
        aria-busy="true"
      >
        <AppSpin size="large" />
      </div>
    );
  }

  // --- Shared: Distance prices (old law right column for both A2 and R2) ---
  const distancePricesBlock = showNewLawJun2024 ? (
    <>
      <InfoRow
        label="4km以下"
        value={formatNumber(display.distance_price4)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="4.1km～16km"
        value={formatNumber(display.distance_price16)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
    </>
  ) : (
    <>
      <InfoRow
        label="2km以下"
        value={formatNumber(display.distance_price2)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="2.1km～4km"
        value={formatNumber(display.distance_price4)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="4.1km～6km"
        value={formatNumber(display.distance_price6)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="6.1km～8km"
        value={formatNumber(display.distance_price8)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="8.1km～10km"
        value={formatNumber(display.distance_price10)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="10.1km～12km"
        value={formatNumber(display.distance_price12)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="12.1km～14km"
        value={formatNumber(display.distance_price14)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="14.1km～16km"
        value={formatNumber(display.distance_price16)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="往療距離加算2㎞毎"
        value={formatNumber(display.distance_add_price)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
    </>
  );

  // --- A2 columns — same shape as A2 production (medical_expense_prices/list.blade.php).
  // The page shows ONE record, so its treatment_type is the page's 術数: it is appended
  // to the 訪問施術料 labels and drives the ―/価格 split of はり・きゅう / はり / きゅう.
  const a2LeftColumn = (
    <AppCard className="p-2">
      <InfoRow
        label="初検料"
        value={formatNumber(display.initial_inspection_fee)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="はり・きゅう"
        value={treatmentPriceAcuAll}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="はり"
        value={treatmentPriceAcuOnly}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="きゅう"
        value={treatmentPriceAcuOnly}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="電（鍼・温）"
        value={formatNumber(display.electric_treatment_price)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      {showNewLaw && (
        <>
          <InfoRow
            label="往療料"
            value={formatNumber(display.distance_price2)}
            labelWidth="w-1/2"
            classNameLabel="text-gray-800"
            classNameValue="text-right"
          />
          <InfoRow
            label="特別地域加算"
            value={formatNumber(display.special_area_addition_price)}
            labelWidth="w-1/2"
            classNameLabel="text-gray-800"
            classNameValue="text-right"
          />
        </>
      )}
      <InfoRow
        label="施術報告書交付料"
        value={formatNumber(display.treatment_report_price)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      {isRevision2026 && (
        <InfoRow
          label="明細書発行加算"
          value={formatNumber(display.statement_issuance_fee)}
          labelWidth="w-1/2"
          classNameLabel="text-gray-800"
          classNameValue="text-right"
        />
      )}
    </AppCard>
  );

  // Right column: the 訪問施術料 ladder once the bands exist, the 往療 distance ladder before.
  // A band with no rate is skipped rather than printed as 0 — 訪問施術料5 only exists from
  // 令和8年法改正, so a 2024 period must not grow an empty ⑤ row.
  const a2RightColumn = (
    <AppCard className="p-2">
      {showNewLaw
        ? ([1, 2, 3, 4, 5] as const).map(band => {
            const price = display[`visit_treatment_price${band}` as keyof MedicalExpensePrice] as
              | number
              | null
              | undefined;
            if (!price) return null;
            return (
              <InfoRow
                key={band}
                label={`訪問施術料${circledBand[band - 1]} ${display.treatment_type}術`}
                value={formatNumber(price)}
                labelWidth="w-1/2"
                classNameLabel="text-gray-800"
                classNameValue="text-right"
              />
            );
          })
        : distancePricesBlock}
    </AppCard>
  );

  // --- R2 Left Column ---
  const r2LeftColumn = (
    <AppCard className="p-2">
      {showNewLaw ? (
        <>
          <InfoRow
            label="マッサージ　１局所"
            value={formatNumber(display.massage_non_inclusion_price1)}
            labelWidth="w-1/2"
            classNameLabel="text-gray-800"
            classNameValue="text-right"
          />
          <InfoRow
            label="マッサージ　２局所"
            value={formatNumber(display.massage_non_inclusion_price2)}
            labelWidth="w-1/2"
            classNameLabel="text-gray-800"
            classNameValue="text-right"
          />
          <InfoRow
            label="マッサージ　３局所"
            value={formatNumber(display.massage_non_inclusion_price3)}
            labelWidth="w-1/2"
            classNameLabel="text-gray-800"
            classNameValue="text-right"
          />
          <InfoRow
            label="マッサージ　４局所"
            value={formatNumber(display.massage_non_inclusion_price4)}
            labelWidth="w-1/2"
            classNameLabel="text-gray-800"
            classNameValue="text-right"
          />
          <InfoRow
            label="マッサージ　５局所"
            value={formatNumber(display.massage_non_inclusion_price5)}
            labelWidth="w-1/2"
            classNameLabel="text-gray-800"
            classNameValue="text-right"
          />
        </>
      ) : (
        <InfoRow
          label="マッサージ"
          value={formatNumber(display.massage_price)}
          labelWidth="w-1/2"
          classNameLabel="text-gray-800"
          classNameValue="text-right"
        />
      )}
      <InfoRow
        label="変形徒手矯正術"
        value={formatNumber(display.orthomorphia_price)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="温罨法"
        value={formatNumber(display.hot_treatment_price)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="電療"
        value={formatNumber(display.electric_treatment_price)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      {showNewLaw && (
        <>
          {showNewLawJun2024 ? (
            <>
              <InfoRow
                label="4km以下"
                value={formatNumber(display.distance_price4)}
                labelWidth="w-1/2"
                classNameLabel="text-gray-800"
                classNameValue="text-right"
              />
              <InfoRow
                label="4.1km～16km"
                value={formatNumber(display.distance_price16)}
                labelWidth="w-1/2"
                classNameLabel="text-gray-800"
                classNameValue="text-right"
              />
            </>
          ) : (
            <InfoRow
              label="往療料"
              value={formatNumber(display.distance_price2)}
              labelWidth="w-1/2"
              classNameLabel="text-gray-800"
              classNameValue="text-right"
            />
          )}
          <InfoRow
            label="特別地域加算料"
            value={formatNumber(display.special_area_addition_price)}
            labelWidth="w-1/2"
            classNameLabel="text-gray-800"
            classNameValue="text-right"
          />
        </>
      )}
      <InfoRow
        label="施術報告書交付料"
        value={formatNumber(display.treatment_report_price)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      {isRevision2026 && (
        <InfoRow
          label="明細書発行加算"
          value={formatNumber(display.statement_issuance_fee)}
          labelWidth="w-1/2"
          classNameLabel="text-gray-800"
          classNameValue="text-right"
        />
      )}
    </AppCard>
  );

  // --- R2 Right Column ---
  const r2RightColumn = (
    <AppCard className="p-2">
      {showNewLaw ? (
        <>
          {visitTreatmentBands.map(tier =>
            ([1, 2, 3, 4, 5] as const).map(region => {
              const key = `visit_treatment_price${tier}_${region}` as keyof typeof display;
              return (
                <InfoRow
                  key={key}
                  label={`訪問施術料${['⓵', '⓶', '⓷', '⓸', '⓹'][tier - 1]}　${region}局所`}
                  value={formatNumber(display[key] as number | null)}
                  labelWidth="w-1/2"
                  classNameLabel="text-gray-800"
                  classNameValue="text-right"
                />
              );
            })
          )}
        </>
      ) : (
        distancePricesBlock
      )}
    </AppCard>
  );

  return (
    <div className="relative">
      {isFetching && (
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-md bg-white/60"
          aria-busy="true"
        >
          <AppSpin size="large" />
        </div>
      )}

      <AppSpace orientation="vertical" size="large" className="w-full">
        <CollapsibleCard title="健康保険料金">
          <div className="flex items-center gap-4 px-4 sm:w-[75%] md:w-[70%] lg:w-[60%] xl:w-1/2 2xl:w-1/3">
            <AppTypography.Text className="text-gray-700 whitespace-nowrap">
              適用期間
            </AppTypography.Text>
            <AppTextField
              className="bg-gray-100 border rounded"
              value={formatAvailableDate(display.available_from)}
              disabled
            />
            <AppTypography.Text className="text-center">～</AppTypography.Text>
            <AppTextField
              className="bg-gray-100 border rounded"
              value={formatAvailableDate(display.available_to)}
              disabled
            />
          </div>

          <AppRow className="justify-center mt-4">
            <AppCol>
              <AppPagination
                current={page}
                total={data?.data.pagination.total ?? 0}
                pageSize={1}
                onChange={p => setPage(p)}
              />
            </AppCol>
          </AppRow>
        </CollapsibleCard>

        <AppRow gutter={[24, 24]}>
          <AppCol xs={24} lg={12}>
            {isA2 ? a2LeftColumn : r2LeftColumn}
          </AppCol>
          <AppCol xs={24} lg={12}>
            {isA2 ? a2RightColumn : r2RightColumn}
          </AppCol>
        </AppRow>
      </AppSpace>
    </div>
  );
};

export default MedicalExpensePricePage;
